import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
    Users, Plus, Loader2, Save, X, User as UserIcon, AlertTriangle, ChevronRight, GripVertical,
    Crosshair, Brain, Zap, Swords, Ghost, RotateCcw
} from 'lucide-react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { DEFAULT_AVATAR } from '../lib/constants';
import { DndContext, useDraggable, useDroppable, DragOverlay } from '@dnd-kit/core';
import type { DragEndEvent, DragStartEvent } from '@dnd-kit/core';

const API = import.meta.env.VITE_API_URL || 'http://localhost:3001';

interface TeamRosterWizardProps {
    teamId: string;
    initialRoster?: any[];
    onComplete: (playersToImport: any[], resetPermissions?: boolean) => void;
    onBack?: () => void;
    mode?: 'setup' | 'edit';
}

const ACTIVE_ROSTER_SLOTS = [
    { id: 'roster-0', csRole: 'AWP', label: 'Open AWP Slot', Icon: <Crosshair size={20} /> },
    { id: 'roster-1', csRole: 'IGL', label: 'Open IGL Slot', Icon: <Brain size={20} /> },
    { id: 'roster-2', csRole: 'Sup', label: 'Open SUP Slot', Icon: <Zap size={20} /> },
    { id: 'roster-3', csRole: 'Entry', label: 'Open ENTRY Slot', Icon: <Swords size={20} /> },
    { id: 'roster-4', csRole: 'Lurk', label: 'Open LURK Slot', Icon: <Ghost size={20} /> }
];

const STAFF_SLOTS = [
    { id: 'staff-0', role: 'Coach', label: 'Open Coach Slot' },
    { id: 'staff-1', role: 'Manager', label: 'Open Manager Slot' }
];

const STANDIN_SLOTS = [0, 1, 2, 3, 4].map(i => ({ id: `standin-${i}`, role: 'Standin', label: `Open Stand-in Slot` }));

function PlayerCard({ player, isAssigned = false, onQuickAssign, stretch = false, isDragging = false, isOverlay = false, setNodeRef, listeners, attributes, style }: any) {
    return (
        <div 
            ref={setNodeRef} 
            style={style}
            {...listeners}
            {...attributes}
            className={`relative flex items-center gap-2.5 p-2 rounded-xl transition-colors ${isOverlay ? 'cursor-grabbing' : 'cursor-grab active:cursor-grabbing'} border ${stretch || isOverlay ? 'h-full w-full' : ''} ${
                isOverlay ? 'bg-slate-700/90 border-slate-500 shadow-2xl opacity-100 z-50 scale-105' :
                isDragging ? 'bg-slate-700/80 border-slate-500 shadow-xl opacity-30 z-50' : 
                isAssigned ? 'bg-slate-800/80 hover:bg-slate-700 border-slate-600/50' :
                'bg-slate-800/40 hover:bg-slate-800 border-slate-700/50'
            }`}
        >
            <div className="text-slate-500 flex-shrink-0 pointer-events-none">
                <GripVertical size={14} />
            </div>
            <img src={player.avatar || DEFAULT_AVATAR} className="w-8 h-8 rounded-full object-cover border border-slate-700 shrink-0 pointer-events-none" alt="" />
            <div className="flex-1 min-w-0 pr-2 pb-0.5 pointer-events-none">
                <div className="text-xs font-semibold text-slate-200 truncate leading-none mb-1 mt-0.5">{player.nickname}</div>
                <div className="flex items-center gap-1.5 mt-1 text-[10px] font-mono leading-none font-bold text-orange-500/90">
                    <img src="/icons/socials/Icon_Faceit.png" alt="Faceit" className="w-[10px] h-[10px] opacity-90 grayscale" />
                    {player.faceit_elo || player.faceitElo ? (
                        <span>{player.faceit_elo || player.faceitElo} ELO</span>
                    ) : (
                        <span className="text-slate-500 grayscale">{player.skill_level ? `LVL ${player.skill_level}` : 'UNRANKED'}</span>
                    )}
                </div>
            </div>
            {onQuickAssign && !isAssigned && !isOverlay && (
                <button 
                    onPointerDown={(e) => { e.stopPropagation(); onQuickAssign(); }}
                    className="w-6 h-6 rounded bg-blue-500/10 text-blue-500 hover:bg-blue-500 hover:text-white flex flex-shrink-0 items-center justify-center transition-colors mr-1"
                >
                    <Plus size={14} />
                </button>
            )}
        </div>
    );
}

function DraggablePlayer({ player, isAssigned = false, onQuickAssign, stretch = false }: { player: any, isAssigned?: boolean, onQuickAssign?: () => void, stretch?: boolean }) {
    const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
        id: player.user_id,
        data: player
    });

    const style = isDragging ? { zIndex: 50 } : undefined;

    return <PlayerCard player={player} isAssigned={isAssigned} onQuickAssign={onQuickAssign} stretch={stretch} isDragging={isDragging} setNodeRef={setNodeRef} listeners={listeners} attributes={attributes} style={style} />;
}

function DroppableSlot({ slotId, label, roleIcon, activePlayer, onRemove }: { slotId: string, label: string, roleIcon?: React.ReactNode, activePlayer: any | null, onRemove: () => void }) {
    const { isOver, setNodeRef } = useDroppable({ id: slotId });

    return (
        <div 
            ref={setNodeRef} 
            className={`h-16 rounded-xl flex items-center transition-all relative ${
                isOver ? 'bg-blue-900/20 border border-blue-500' :
                activePlayer ? 'border border-transparent' : 
                'bg-slate-900/50 border border-slate-800 border-dashed hover:border-slate-700'
            }`}
        >
            {/* Context Badge (Left Side Role Icon) */}
            {roleIcon && (
                <div className="absolute -left-[54px] top-1/2 -translate-y-1/2 w-10 h-16 bg-slate-950 border border-slate-800/50 rounded-lg flex flex-col items-center justify-center text-slate-500 shadow-sm opacity-90 overflow-hidden">
                    {roleIcon}
                </div>
            )}

            {activePlayer ? (
                <div className="flex w-full h-full items-center gap-3">
                    <div className="flex-1 min-w-0 h-full">
                        <DraggablePlayer player={activePlayer} isAssigned={true} stretch={true} />
                    </div>
                    <div className="w-12 h-full flex items-center justify-center shrink-0">
                        <button 
                            onClick={(e) => { e.stopPropagation(); e.preventDefault(); onRemove(); }}
                            className="relative z-50 pointer-events-auto w-8 h-8 rounded-full bg-slate-800/80 hover:bg-red-500/10 text-slate-500 hover:text-red-500 hover:scale-110 flex items-center justify-center transition-all shadow-sm border border-slate-700/50"
                            title="Remove assigned player"
                        >
                            <X strokeWidth={3} size={14} />
                        </button>
                    </div>
                </div>
            ) : (
                <div className="flex px-5 items-center gap-4 w-full opacity-40">
                    <div className="w-9 h-9 rounded-full bg-slate-800 flex items-center justify-center border border-slate-700 border-dashed">
                        <UserIcon size={14} className="text-slate-500" />
                    </div>
                    <div className="flex-1 text-sm font-medium text-slate-500 italic truncate">
                        {label}
                    </div>
                </div>
            )}
        </div>
    );
}


export function TeamRosterWizard({ teamId, initialRoster = [], onComplete, onBack, mode = 'setup' }: TeamRosterWizardProps) {
    const { t } = useTranslation();
    const [exiting, setExiting] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    
    const [suggestions, setSuggestions] = useState<any[]>([]);
    const [activeDragId, setActiveDragId] = useState<string | null>(null);
    const [fetchingSuggestions, setFetchingSuggestions] = useState(true);
    const [resetPermissions, setResetPermissions] = useState(false);

    const [slots, setSlots] = useState<Record<string, any | null>>({});

    useEffect(() => {
        if (error) {
            const t = setTimeout(() => setError(''), 5000);
            return () => clearTimeout(t);
        }
    }, [error]);

    // Stabilize initialRoster reference to prevent infinite re-fetch loops
    const initialRosterRef = React.useRef(initialRoster);
    const hasFetched = React.useRef(false);

    useEffect(() => {
        if (hasFetched.current) return;
        hasFetched.current = true;

        const fetchSuggestions = async () => {
            try {
                const token = localStorage.getItem('token');
                const res = await axios.get(`${API}/teams/${teamId}/roster-suggestions`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                
                const roster = initialRosterRef.current;
                // If there's an initial roster, slot them into their current positions
                if (roster && roster.length > 0) {
                    const initialSlots: Record<string, any | null> = {};
                    let pool = res.data || [];
                    
                    roster.forEach(ir => {
                        // find matching slot depending on role/csRole
                        let targetSlotId = null;
                        if (ir.role === 'Player') {
                            const match = ACTIVE_ROSTER_SLOTS.find(s => s.csRole === ir.csRole);
                            if (match) targetSlotId = match.id;
                        } else if (ir.role === 'Coach') {
                            targetSlotId = 'staff-0';
                        } else if (ir.role === 'Manager') {
                            targetSlotId = 'staff-1';
                        } else if (ir.role === 'Standin' || ir.role === 'Stand-in') {
                            const emptyStandin = STANDIN_SLOTS.find(s => !initialSlots[s.id]);
                            if (emptyStandin) targetSlotId = emptyStandin.id;
                        }

                        if (targetSlotId && !initialSlots[targetSlotId]) {
                            // Use the Faceit pool data for this player if available (has richer data)
                            const poolMatch = pool.find((p: any) => p.user_id === ir.user_id);
                            initialSlots[targetSlotId] = poolMatch || ir;
                            pool = pool.filter((p: any) => p.user_id !== ir.user_id);
                        }
                    });

                    setSlots(initialSlots);
                    setSuggestions(pool);
                } else {
                    setSuggestions(res.data || []);
                }
            } catch (err: any) {
                console.warn('Could not fetch faceit roster suggestions', err);
            } finally {
                setFetchingSuggestions(false);
            }
        };
        fetchSuggestions();
    }, [teamId]);

    const handleDragStart = (event: DragStartEvent) => {
        setActiveDragId(event.active.id as string);
    };

    const handleDragEnd = (event: DragEndEvent) => {
        setActiveDragId(null);
        const { active, over } = event;
        if (!over) return;
        
        const playerId = active.id as string;
        const overId = over.id as string;
        
        // Find player
        let source = 'suggestions';
        let playerObj = suggestions.find(p => p.user_id === playerId);
        
        if (!playerObj) {
            for (const [sId, p] of Object.entries(slots)) {
                if (p && p.user_id === playerId) {
                    playerObj = p;
                    source = sId;
                    break;
                }
            }
        }
        
        if (!playerObj || source === overId) return;

        if (overId === 'suggestions-pool') {
            // Drop back to suggestions
            setSuggestions(prev => [...prev, playerObj]);
            setSlots(prev => ({ ...prev, [source]: null }));
        } else {
            // Drop onto slot
            const existingPlayerInSlot = slots[overId];
            
            const newSlots = { ...slots, [overId]: playerObj };
            
            if (source === 'suggestions') {
                let newSuggestions = suggestions.filter(p => p.user_id !== playerId);
                if (existingPlayerInSlot) {
                    newSuggestions.push(existingPlayerInSlot);
                }
                setSuggestions(newSuggestions);
            } else {
                newSlots[source] = existingPlayerInSlot;
            }
            
            setSlots(newSlots);
        }
    };

    const handleQuickAssign = (player: any) => {
        // Try active roster, then stand-ins, then staff
        const firstEmptyActive = ACTIVE_ROSTER_SLOTS.find(s => !slots[s.id]);
        if (firstEmptyActive) {
            setSlots(prev => ({ ...prev, [firstEmptyActive.id]: player }));
            setSuggestions(prev => prev.filter(p => p.user_id !== player.user_id));
            return;
        }

        const firstEmptyStandin = STANDIN_SLOTS.find(s => !slots[s.id]);
        if (firstEmptyStandin) {
            setSlots(prev => ({ ...prev, [firstEmptyStandin.id]: player }));
            setSuggestions(prev => prev.filter(p => p.user_id !== player.user_id));
            return;
        }

        setError('No obvious empty slots found. Drag and drop to replace!');
    };

    const handleRemoveFromSlot = (slotId: string) => {
        const player = slots[slotId];
        if (player) {
            setSuggestions(prev => [player, ...prev]);
            setSlots(prev => ({ ...prev, [slotId]: null }));
        }
    };

    const handleSaveAndFinish = () => {
        const playersToImport: any[] = [];
        
        for (const [slotId, player] of Object.entries(slots)) {
            if (!player) continue;
            let role = 'Player';
            let csRole = null;
            
            if (slotId.startsWith('roster-')) {
                role = 'Player';
                csRole = ACTIVE_ROSTER_SLOTS.find(s => s.id === slotId)?.csRole;
            } else if (slotId.startsWith('staff-')) {
                role = STAFF_SLOTS.find(s => s.id === slotId)?.role || 'Manager';
            } else if (slotId.startsWith('standin-')) {
                role = 'Standin';
            }

            playersToImport.push({
                ...player,
                role,
                csRole
            });
        }

        setExiting(true);
        setTimeout(() => onComplete(playersToImport, resetPermissions), 300);
    };

    const handleSkip = () => {
        setExiting(true);
        setTimeout(() => onComplete([]), 300);
    };

    const handleClearAll = () => {
        const resetSlots = { ...slots };
        const returnedPlayers: any[] = [];
        
        for (const [key, player] of Object.entries(resetSlots)) {
            if (player) {
                returnedPlayers.push(player);
                resetSlots[key] = null;
            }
        }
        
        if (returnedPlayers.length > 0) {
            setSlots(resetSlots);
            setSuggestions(prev => [...returnedPlayers, ...prev]);
        }
    };

    const playersAssignedCount = Object.values(slots).filter(Boolean).length;
    
    // Droppable pool for pulling players back
    const { setNodeRef: setPoolRef, isOver: isPoolOver } = useDroppable({ id: 'suggestions-pool' });

    const activeDragPlayer = React.useMemo(() => {
        if (!activeDragId) return null;
        const inPool = suggestions.find(p => p.user_id === activeDragId);
        if (inPool) return inPool;
        for (const p of Object.values(slots)) {
            if (p && p.user_id === activeDragId) return p;
        }
        return null;
    }, [activeDragId, suggestions, slots]);

    return (
        <DndContext 
            onDragStart={handleDragStart} 
            onDragEnd={handleDragEnd}
            onDragCancel={() => setActiveDragId(null)}
        >
            <div className={`team-roster-setup-card w-[90vw] max-w-[1500px] h-[750px] bg-slate-900/60 backdrop-blur-2xl border border-slate-700/50 rounded-2xl shadow-[0_0_80px_rgba(0,0,0,0.8)] flex flex-col overflow-hidden relative transition-all duration-300 ${exiting ? 'opacity-0' : 'opacity-100'}`}>
                    
                    {/* Header */}
                    <div className="flex-shrink-0 px-8 py-4 border-b border-slate-700/40 bg-slate-900/40 text-center relative z-10 block pointer-events-auto shadow-sm">
                        {mode === 'edit' && onBack && (
                            <button
                                onClick={() => { setExiting(true); setTimeout(() => onBack(), 300); }}
                                className="absolute top-4 right-4 w-8 h-8 rounded-lg bg-slate-800 hover:bg-red-500/20 border border-slate-700 hover:border-red-500/40 text-slate-400 hover:text-red-400 flex items-center justify-center transition-all z-20"
                            >
                                <X size={16} strokeWidth={2.5} />
                            </button>
                        )}
                        <h2 className="text-xl font-bold text-white mb-1 tracking-tight">
                            {mode === 'edit' ? 'Roster Configuration' : 'Active Roster Setup'}
                        </h2>
                        <p className="text-slate-400 text-xs max-w-xl mx-auto">
                            Drag and assign Faceit Team members to your specialized Roster Slots. Setting proper roles enables the Stratbook logic to function automatically.
                        </p>
                    </div>

                    {/* Content */}
                    <div className="flex-1 flex flex-row min-h-0 bg-[url('/grid-pattern.svg')] bg-[length:24px_24px]">
                        
                        {/* Left: Suggestions Pool (Green Box) */}
                        <div 
                            ref={setPoolRef}
                            className={`w-[350px] flex flex-col flex-shrink-0 border-r border-slate-700/50 bg-transparent relative transition-colors ${isPoolOver ? 'bg-slate-800/20' : ''}`}
                        >
                            <div className="p-5 border-b border-slate-800">
                                <div className="flex items-center justify-between mb-1">
                                    <h3 className="text-sm font-semibold text-slate-300 flex items-center gap-2">
                                        <Users size={16} className="text-blue-400" />
                                        Faceit Team Members Pool
                                    </h3>
                                    <button 
                                        onClick={handleClearAll}
                                        className="text-[10px] font-bold uppercase tracking-widest px-2.5 py-1.5 bg-slate-800 hover:bg-slate-700 hover:text-white text-slate-400 rounded-lg transition-colors border border-slate-700 shadow-sm flex items-center gap-1.5 opacity-90 hover:opacity-100"
                                        title="Unassign All Players"
                                    >
                                        <RotateCcw size={12} />
                                        Clear All
                                    </button>
                                </div>
                                <span className="text-xs bg-slate-800 text-slate-400 px-2 py-0.5 rounded font-mono border border-slate-700 mt-2 inline-block">
                                    {suggestions.length} Unassigned
                                </span>
                            </div>

                            <div className="flex-1 overflow-y-auto p-5 custom-scrollbar relative">
                                {fetchingSuggestions ? (
                                    <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 flex flex-col items-center justify-center text-slate-500">
                                        <Loader2 size={24} className="animate-spin mb-2" />
                                        <span className="text-[11px] uppercase tracking-wider font-bold">Fetching Members...</span>
                                    </div>
                                ) : suggestions.length === 0 ? (
                                    <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 flex flex-col items-center justify-center text-slate-500 px-4 text-center">
                                        <p className="text-sm font-semibold text-slate-400">All players assigned.</p>
                                        <p className="text-xs mt-0.5 opacity-70">Drag assigned players back here to remove them.</p>
                                    </div>
                                ) : (
                                    <div className="flex flex-col gap-3">
                                        {suggestions.map((p) => (
                                            <div key={p.user_id} className="w-full">
                                                <DraggablePlayer player={p} onQuickAssign={() => handleQuickAssign(p)} />
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Middle: Active Roster (Purple Box 1) */}
                        <div className="flex-[3] p-8 border-r border-slate-700/40 relative flex flex-col h-full bg-transparent">
                            <div className="flex items-center justify-between mb-7 pl-[54px]">
                                <h3 className="text-white font-bold text-lg flex items-center gap-2">
                                    Active Roster
                                </h3>
                                <span className="text-xs font-mono text-slate-400 font-bold bg-slate-800/80 border border-slate-700/50 px-2 py-1 rounded-md shadow-inner">
                                    {Object.keys(slots).filter(k => k.startsWith('roster-') && slots[k]).length}/5
                                </span>
                            </div>

                            <div className="space-y-4 pl-[54px] flex-1">
                                {ACTIVE_ROSTER_SLOTS.map((slot) => {
                                    return (
                                        <DroppableSlot 
                                            key={slot.id}
                                            slotId={slot.id}
                                            label={slot.label}
                                                roleIcon={
                                                    <div className="flex flex-col items-center justify-center gap-1.5 opacity-80 text-slate-400">
                                                        {slot.Icon}
                                                        <span className="text-[9px] font-black tracking-widest">{slot.csRole}</span>
                                                    </div>
                                                }
                                            activePlayer={slots[slot.id]}
                                            onRemove={() => handleRemoveFromSlot(slot.id)}
                                        />
                                    )
                                })}
                            </div>
                        </div>

                        {/* Right: Team Staff & Stand-ins (Purple Box 2) */}
                        <div className="flex-[6] flex flex-col h-full overflow-hidden bg-transparent">
                            
                            {/* Team Staff Area */}
                            <div className="p-8 pb-6 border-b border-slate-700/40">
                                <h3 className="text-slate-200 font-bold text-lg mb-5 flex items-center justify-between">
                                    Team Staff
                                    <span className="text-xs font-mono text-slate-400 bg-slate-800/80 border border-slate-700/50 px-2.5 py-1 rounded shadow-inner font-medium">Optional</span>
                                </h3>
                                <div className="grid grid-cols-2 gap-4">
                                    {STAFF_SLOTS.map(slot => (
                                        <DroppableSlot 
                                            key={slot.id}
                                            slotId={slot.id}
                                            label={slot.label}
                                            activePlayer={slots[slot.id]}
                                            onRemove={() => handleRemoveFromSlot(slot.id)}
                                        />
                                    ))}
                                </div>
                            </div>

                            {/* Stand-ins Area */}
                            <div className="flex-1 p-8 pt-6 overflow-y-auto custom-scrollbar">
                                <div className="flex items-center justify-between mb-5">
                                    <h3 className="text-slate-200 font-bold text-lg">
                                        Stand-ins
                                    </h3>
                                    <span className="text-xs font-mono text-slate-400 bg-slate-800/80 border border-slate-700/50 px-2.5 py-1 rounded shadow-inner font-medium">Backup</span>
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    {STANDIN_SLOTS.map(slot => (
                                        <DroppableSlot 
                                            key={slot.id}
                                            slotId={slot.id}
                                            label={slot.label}
                                            activePlayer={slots[slot.id]}
                                            onRemove={() => handleRemoveFromSlot(slot.id)}
                                        />
                                    ))}
                                </div>
                            </div>

                        </div>
                    </div>

                    {/* Footer */}
                    <div className="flex-shrink-0 p-5 border-t border-slate-700/40 bg-slate-900/40 flex items-center justify-between relative z-10">
                        <div className="flex-1 flex items-center gap-4">
                            {mode === 'setup' && onBack && (
                                <button 
                                    className="team-wizard-back px-6 py-2.5 text-xs font-bold uppercase tracking-wider text-slate-400 hover:text-white transition-colors"
                                    onClick={() => {
                                        setExiting(true);
                                        setTimeout(() => onBack(), 300);
                                    }}
                                    disabled={loading}
                                >
                                    Go Back
                                </button>
                            )}
                            {error && (
                                <div className="bg-red-500/10 border border-red-500/30 text-red-500 px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-wider flex items-center gap-2 w-max animate-in fade-in slide-in-from-bottom-2">
                                    <AlertTriangle size={14} />
                                    {error}
                                </div>
                            )}
                        </div>
                        <div className="flex shrink-0 items-center justify-end">
                            {mode === 'edit' && (
                                <div className="mr-6 flex items-center gap-2">
                                    <input 
                                        type="checkbox" 
                                        id="resetPermissions"
                                        checked={resetPermissions}
                                        onChange={(e) => setResetPermissions(e.target.checked)}
                                        className="w-4 h-4 rounded border-slate-700 bg-slate-900 text-emerald-500 cursor-pointer focus:ring-emerald-500/20"
                                    />
                                    <label htmlFor="resetPermissions" className="text-xs font-semibold text-slate-400 cursor-pointer select-none">
                                        Reset all individual permissions
                                    </label>
                                </div>
                            )}
                        {mode === 'edit' ? (
                            <button 
                                className="flex items-center gap-2.5 bg-emerald-600 hover:bg-emerald-500 text-white px-9 py-3 rounded-xl font-black transition-colors disabled:opacity-50 shadow-[0_0_20px_rgba(16,185,129,0.3)] uppercase tracking-wider text-xs border border-emerald-500/50"
                                onClick={handleSaveAndFinish}
                                disabled={loading}
                            >
                                {loading ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                                Save Changes
                            </button>
                        ) : playersAssignedCount === 0 ? (
                            <button 
                                className="team-wizard-skip text-xs uppercase text-slate-400 hover:text-white px-8 py-3 bg-slate-800 hover:bg-slate-700 rounded-xl font-black tracking-wider transition-all border border-slate-700 shadow-md"
                                onClick={handleSkip}
                                disabled={loading}
                            >
                                Skip for now
                            </button>
                        ) : (
                            <button 
                                className="flex items-center gap-2.5 bg-blue-600 hover:bg-blue-500 text-white px-9 py-3 rounded-xl font-black transition-colors disabled:opacity-50 shadow-[0_0_20px_rgba(37,99,235,0.3)] uppercase tracking-wider text-xs border border-blue-500/50"
                                onClick={handleSaveAndFinish}
                                disabled={loading}
                            >
                                {loading ? <Loader2 size={16} className="animate-spin" /> : <ChevronRight size={16} />}
                                Continue Setup
                            </button>
                        )}
                        </div>
                    </div>

            </div>

            {typeof document !== 'undefined' ? createPortal(
                <DragOverlay dropAnimation={null} modifiers={[]}>
                    {activeDragPlayer ? (
                        <div className="w-[310px] shadow-2xl opacity-100 cursor-grabbing z-[1000] drop-shadow-2xl">
                            <PlayerCard player={activeDragPlayer} isAssigned={true} isOverlay={true} />
                        </div>
                    ) : null}
                </DragOverlay>,
                document.body
            ) : null}
        </DndContext>
    );
}
